import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Database, ChevronRight, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Temporary Super Admin action — runs the curriculum canonicalization
 * migration against the current environment's DB (preview or prod, whichever
 * is serving the API). Two-step: dry-run preview → explicit confirm → execute.
 *
 * Mounted only for Super Admins from `Admin.jsx`. Uses the shared backend
 * mapping logic (`services.curriculum_migration.compute_updates`) so results
 * match the CLI script that already ran successfully on preview.
 */
export default function CurriculumMigrationPanel() {
  const [phase, setPhase] = useState("idle"); // idle | dry-loading | dry-ready | exec-loading | done
  const [dry, setDry] = useState(null);
  const [result, setResult] = useState(null);

  const runDryRun = async () => {
    setPhase("dry-loading");
    setDry(null); setResult(null);
    try {
      const r = await api.get("/admin/curriculum-migration/dry-run");
      setDry(r.data);
      setPhase("dry-ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run gagal.");
      setPhase("idle");
    }
  };

  const runExecute = async () => {
    if (!dry || dry.to_update === 0) return;
    setPhase("exec-loading");
    try {
      const r = await api.post("/admin/curriculum-migration/execute");
      setResult(r.data);
      setPhase("done");
      toast.success(`Migrasi selesai — ${r.data.updated} sekolah diperbarui.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migrasi gagal.");
      setPhase("dry-ready");
    }
  };

  const reset = () => { setPhase("idle"); setDry(null); setResult(null); };

  return (
    <div
      className="rounded-2xl border border-[#F59E0B] bg-[#FFFBEB] p-4 mb-4"
      data-testid="curriculum-migration-panel"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#F59E0B] text-white flex items-center justify-center flex-shrink-0">
          <Database size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#78350F]">
            Migrasi Kurikulum ke Canonical Taxonomy
            <span className="ml-2 inline-block text-[10px] uppercase tracking-widest bg-[#F59E0B] text-white px-1.5 py-0.5 rounded">Super Admin</span>
          </div>
          <p className="text-xs text-[#78350F]/80 mt-1">
            Menormalkan field <code className="text-[11px] bg-white px-1 rounded">curriculum</code> ke 15 nilai canonical.
            Backup lengkap disimpan di <code className="text-[11px] bg-white px-1 rounded">curriculum_legacy</code> tiap dokumen.
            Preview data lebih dulu, lalu konfirmasi eksekusi.
          </p>

          {/* IDLE / initial */}
          {phase === "idle" && (
            <div className="mt-3">
              <Button
                data-testid="curriculum-migration-dry-run"
                onClick={runDryRun}
                className="rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-white h-9 px-4"
              >
                Preview Migrasi (Dry-Run)
              </Button>
            </div>
          )}

          {phase === "dry-loading" && (
            <div className="mt-3 flex items-center gap-2 text-xs text-[#78350F]">
              <Loader2 size={14} className="animate-spin" /> Menghitung rencana migrasi…
            </div>
          )}

          {/* DRY-RUN RESULT */}
          {(phase === "dry-ready" || phase === "exec-loading") && dry && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Stat label="Total sekolah" value={dry.total_schools} testid="dry-total" />
                <Stat label="Akan diupdate" value={dry.to_update} highlight testid="dry-to-update" />
                <Stat label="Sudah canonical" value={dry.unchanged} testid="dry-unchanged" />
                <Stat label="Unmapped/unsafe" value={dry.unsafe_count} warn={dry.unsafe_count > 0} testid="dry-unsafe" />
              </div>

              {dry.unsafe_count > 0 && (
                <div className="text-[11px] bg-[#FEF3C7] border border-[#F59E0B] rounded-md p-2 flex items-start gap-2" data-testid="dry-unsafe-warning">
                  <AlertTriangle size={12} className="text-[#B45309] mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Ada {dry.unsafe_count} nilai yang tidak bisa dipetakan aman.</strong>
                    {" "}Nilai asli tetap dilestarikan ke Detail Kurikulum + ditandai <em>Lainnya</em>.
                    {" "}Contoh: {dry.unsafe_docs.slice(0, 3).map((d) => `${d.name} → ${d.unsafe_values.join(", ")}`).join("; ")}
                  </div>
                </div>
              )}

              {dry.sample_mappings.length > 0 && (
                <div className="mt-1" data-testid="dry-samples">
                  <div className="text-[10px] uppercase tracking-widest text-[#78350F] font-semibold mb-1">
                    Contoh mapping ({dry.sample_mappings.length} dari {dry.to_update})
                  </div>
                  <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
                    {dry.sample_mappings.map((m, i) => (
                      <div key={m.school_id} className="rounded-md bg-white border border-[#EAE7E0] p-2 text-[11px]" data-testid={`dry-sample-${i}`}>
                        <div className="font-semibold text-[#1F2937] truncate">{m.name}</div>
                        <div className="mt-0.5 flex items-center gap-1 text-[#4B5563] flex-wrap">
                          <span className="text-[#B45309]">{JSON.stringify(m.from)}</span>
                          <ChevronRight size={11} />
                          <span className="text-[#065F46]">{JSON.stringify(m.to)}</span>
                        </div>
                        {m.detail_added?.length > 0 && (
                          <div className="mt-0.5 text-[#78350F]">
                            + Detail dilestarikan: {m.detail_added.join(", ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {dry.to_update === 0 && (
                <div className="rounded-md bg-[#ECFDF5] border border-[#065F46] p-2 text-[11px] text-[#065F46] flex items-center gap-2" data-testid="dry-already-canonical">
                  <CheckCircle2 size={14} /> Semua sekolah sudah canonical. Tidak ada yang perlu diupdate.
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button
                  data-testid="curriculum-migration-confirm"
                  onClick={runExecute}
                  disabled={dry.to_update === 0 || phase === "exec-loading"}
                  className="rounded-full bg-[#065F46] hover:bg-[#047857] text-white h-9 px-4 disabled:opacity-50"
                >
                  {phase === "exec-loading" ? (<><Loader2 size={13} className="animate-spin mr-1" /> Menjalankan…</>) : `Konfirmasi Migrasi (${dry.to_update} sekolah)`}
                </Button>
                <Button variant="outline" onClick={reset} data-testid="curriculum-migration-reset" className="rounded-full h-9 px-4">
                  Batal
                </Button>
              </div>
            </div>
          )}

          {/* DONE — final report */}
          {phase === "done" && result && (
            <div className="mt-3 space-y-2" data-testid="migration-result">
              <div className="rounded-md bg-[#ECFDF5] border border-[#065F46] p-2 text-[12px] text-[#065F46] flex items-center gap-2">
                <CheckCircle2 size={14} /> Migrasi selesai.
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Stat label="Diperbarui" value={result.updated} highlight testid="result-updated" />
                <Stat label="Tetap" value={result.unchanged} testid="result-unchanged" />
                <Stat label="Gagal" value={result.failed_count} warn={result.failed_count > 0} testid="result-failed" />
                <Stat label="Unsafe" value={result.unsafe_count} warn={result.unsafe_count > 0} testid="result-unsafe" />
              </div>
              {result.failed_count > 0 && (
                <div className="text-[11px] text-[#7C2D12]">
                  Sekolah yang gagal: {result.failed.map((f) => f.school_id).join(", ")}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={runDryRun} data-testid="curriculum-migration-verify" className="rounded-full h-9 px-4">
                  Verifikasi Ulang (Dry-Run)
                </Button>
                <Button variant="outline" onClick={reset} data-testid="curriculum-migration-close" className="rounded-full h-9 px-4">
                  Tutup
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight = false, warn = false, testid }) {
  const cls = highlight
    ? "border-[#065F46] bg-white text-[#065F46]"
    : warn
    ? "border-[#B45309] bg-white text-[#B45309]"
    : "border-[#EAE7E0] bg-white text-[#1F2937]";
  return (
    <div className={`rounded-md border ${cls} p-2`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-lg font-semibold">{Number(value || 0).toLocaleString("id-ID")}</div>
    </div>
  );
}
